import { Flex } from '@radix-ui/themes';
import { CommonDialog } from 'components/common/dialogs';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonToast } from 'components/common/toast';
import { ErrorLevel } from 'lib_ts/enums/errors.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { isNil } from 'lodash';
import { FC, useEffect } from 'react';
import useInboxStore from 'stores/use-inbox-store';

const AUTO_CLOSE_DELAY_MS = 3_000;

export const InboxDialog: FC = () => {
  const dialog = useInboxStore((s) => s.dialog);
  const inbox = useInboxStore((s) => s.inbox);

  // auto-close dialog when inbox becomes empty (e.g. after dismissing the last msg)
  useEffect(() => {
    if (isNil(dialog)) {
      // dialog is not open
      return;
    }

    if (inbox.length > 0) {
      // inbox is not empty
      return;
    }

    const timeout = setTimeout(() => {
      useInboxStore.getState().setDialog(null);
    }, AUTO_CLOSE_DELAY_MS);

    return () => clearTimeout(timeout);
  }, [inbox.length, dialog]);

  if (isNil(dialog)) {
    return null;
  }

  return (
    <ErrorBoundary identifier="InboxDialog">
      <CommonDialog
        key={dialog}
        identifier="InboxDialog"
        width={RADIX.DIALOG.WIDTH.LG}
        title="Inbox"
        content={
          <Flex direction="column" gap={RADIX.FLEX.GAP.LG}>
            {inbox.length === 0 && (
              <CommonToast
                config={{
                  level: ErrorLevel.success,
                  color: RADIX.COLOR.SUCCESS,
                  message_md:
                    'Congratulations, your inbox is empty! This window will close itself shortly...',
                }}
                onDismiss={() => useInboxStore.getState().setDialog(null)}
                withoutToast
              />
            )}

            {inbox.map((n, i) => (
              <CommonToast
                key={n.id ?? `inbox-msg-${i}`}
                config={n}
                onDismiss={() => useInboxStore.getState().removeById(n.id)}
                withoutToast
              />
            ))}
          </Flex>
        }
        buttons={[
          {
            invisible: inbox.length === 0,
            label: 'Clear All',
            color: RADIX.COLOR.DANGER,
            onClick: () => useInboxStore.getState().removeAll(),
          },
        ]}
        onClose={() => useInboxStore.getState().setDialog(null)}
      />
    </ErrorBoundary>
  );
};
